/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { GET_DASHBOARDS } from "./dashboardStore";
import { IcClose, IcDashboard } from "./icons";

/**
 * Centered glass modal for copying a single widget/component onto a
 * DIFFERENT dashboard — picks from the user's other boards instead of
 * rebuilding the same card from scratch there.
 */
export default function CopyToDashboardModal({ open, widgetLabel, excludeId, onCopy, onClose }) {
  const [dashboards, setDashboards] = useState(null); // null = not loaded yet
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [copyingId, setCopyingId] = useState(null);
  const [copyError, setCopyError] = useState("");

  const load = () => {
    setLoadError(false);
    setDashboards(null);
    GET_DASHBOARDS()
      .then((res) => setDashboards(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCopyingId(null);
    setCopyError("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const others = (dashboards || []).filter((d) => String(d._id) !== String(excludeId));
  const list = others.filter(
    (d) => !query || (d.name || "Untitled dashboard").toLowerCase().includes(query.toLowerCase())
  );

  const pick = async (d) => {
    if (copyingId) return;
    setCopyingId(d._id);
    setCopyError("");
    try {
      await onCopy(d);
      onClose();
    } catch (err) {
      setCopyError(
        err?.response?.data?.error?.message || err?.message || "Couldn't copy — try again."
      );
      setCopyingId(null);
    }
  };

  return (
    <div
      className="db-modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !copyingId) onClose();
      }}
    >
      <div className="db-modal" role="dialog" aria-modal="true" aria-label="Copy to dashboard">
        <div className="db-modal-inner">
          <div className="db-modal-head">
            <div>
              <div className="db-modal-t">Copy to dashboard</div>
              <div className="db-modal-s">
                {widgetLabel
                  ? `Add a copy of "${widgetLabel}" to another dashboard.`
                  : "Add a copy of this to another dashboard."}
              </div>
            </div>
            <button
              type="button"
              className="db-iconbtn"
              onClick={onClose}
              aria-label="Close"
              disabled={!!copyingId}
            >
              <IcClose />
            </button>
          </div>

          {dashboards === null && !loadError && (
            <div className="db-mempty">Loading your dashboards…</div>
          )}

          {loadError && (
            <div className="db-mempty">
              Couldn&apos;t load your dashboards.
              <div style={{ marginTop: 10 }}>
                <button type="button" className="db-btn sm" onClick={load}>
                  Retry
                </button>
              </div>
            </div>
          )}

          {dashboards !== null && !loadError && (
            <>
              {others.length > 0 && (
                <input
                  className="db-msearch"
                  placeholder="Search dashboards"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              )}
              <div className="db-mlist">
                {list.length === 0 && (
                  <div className="db-mempty">
                    {others.length === 0
                      ? "You don't have any other dashboards yet. Create one from All dashboards first."
                      : "No dashboards match that search."}
                  </div>
                )}
                {list.map((d) => (
                  <button
                    key={d._id}
                    type="button"
                    className="db-mitem"
                    disabled={!!copyingId}
                    onClick={() => pick(d)}
                  >
                    <span className="ic"><IcDashboard size={16} /></span>
                    <span className="t">{d.name || "Untitled dashboard"}</span>
                    <span className="v">
                      {copyingId === d._id
                        ? "Copying…"
                        : `${(d.widgets || []).length} widget${(d.widgets || []).length === 1 ? "" : "s"}`}
                    </span>
                  </button>
                ))}
              </div>
              {copyError && (
                <div className="db-modal-s" style={{ color: "var(--db-bad)", marginTop: 10 }}>
                  {copyError}
                </div>
              )}
            </>
          )}

          <div className="db-modal-foot">
            <button type="button" className="db-btn ghost" onClick={onClose} disabled={!!copyingId}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

CopyToDashboardModal.propTypes = {
  open: PropTypes.bool,
  widgetLabel: PropTypes.string,
  excludeId: PropTypes.string,
  onCopy: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
